/**
 * Maps the active i18next language onto a BCP-47 tag usable by `Intl`.
 *
 * i18next may report a region-qualified language coming from the browser
 * (`en-GB`, `nl-NL`), so only the base code is matched. Anything we have no
 * dictionary for gets the English tag, mirroring `fallbackLng: 'en'`.
 */
const LOCALE_TAGS: Record<string, string> = {
  hu: 'hu-HU',
  en: 'en-GB',
  sr: 'sr-Latn-RS',
};

export const baseLanguage = (language?: string): string =>
  (language || 'en').split('-')[0].toLowerCase();

export const localeTag = (language?: string): string =>
  LOCALE_TAGS[baseLanguage(language)] || LOCALE_TAGS.en;

/**
 * The tag of the language a panel's *text* is actually rendered in.
 *
 * `fallbackLng` routes `sr` to Hungarian, so a screen the Serbian dictionary
 * does not cover shows Hungarian labels to a Serbian admin. Formatting its
 * dates and numbers as `sr-Latn-RS` would put a Serbian date order under those
 * Hungarian labels — and would change what a Serbian admin sees today, which
 * on those screens is the Hungarian format. Screens that do carry Serbian
 * strings keep using `localeTag`.
 */
export const textLocaleTag = (language?: string): string =>
  baseLanguage(language) === 'sr' ? LOCALE_TAGS.hu : localeTag(language);

/**
 * The base code of the language a panel's *text* is actually rendered in.
 *
 * This is what `<html lang>` has to carry: a screen reader takes its
 * pronunciation from that attribute, so it must name the language of the words
 * on screen rather than the language the admin picked in the switcher. The
 * Serbian dictionary covers 175 keys out of 2099 — every other label falls back
 * to Hungarian, so `lang="sr"` would have Hungarian text read out with Serbian
 * phonetics. Same reasoning as `textLocaleTag`, one step further.
 */
export const textLang = (language?: string): string => baseLanguage(textLocaleTag(language));

/**
 * The dayjs locale for a screen whose text IS fully translated into the
 * selected language.
 *
 * This is the "screens that do carry Serbian strings keep using `localeTag`"
 * case that `textLocaleTag` above names. The raffle (#92) is the first such
 * screen: its key tree is complete in all three languages, so the fallback
 * reasoning inverts — under a Serbian heading it is the HUNGARIAN date that is
 * the mismatch, which is exactly what a Serbian admin saw on the raffle
 * dashboard's day separators.
 *
 * Loading the locale data is the caller's job (`dayjs/locale/sr`); `en` is
 * built into dayjs. Use `dayjsLocale` for any screen that still falls back.
 */
export const translatedDayjsLocale = (language?: string): string =>
  baseLanguage(localeTag(language));

/**
 * The dayjs locale name matching the language a panel's text is rendered in.
 *
 * dayjs names its locales `hu` / `en`, not BCP-47, which is the same base code
 * `textLang` produces. Serbian therefore lands on `hu`, the same place its text
 * does. Loading the locale data (`dayjs/locale/hu`) is still the caller's job;
 * `en` is built into dayjs.
 */
export const dayjsLocale = (language?: string): string => textLang(language);

export default localeTag;
